import { useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { ActionButton } from '../ui/ActionButton.js'
import { notify, notifyError } from '../ui/Toasts.js'
import { useCacheTable, useForm, useSearch } from '../ui/hooks.js'
import { OrtsFeldPaar, StrassenFeld, useOrtschaften, useStrassen } from '../ui/OrtsFelder.js'
import { CUSTOMER_SEARCH } from '../net/search.js'
import {
  createCustomer,
  removeCustomer,
  updateCustomer,
  type CustomerFields
} from '../net/records.js'
import type { CacheRow } from '../net/localDb.js'

const EMPTY: CustomerFields = {
  name: '',
  contact: '',
  street: '',
  zip: '',
  city: '',
  email: '',
  phone: '',
  notes: '',
  oneOff: false
}

export function Customers({ session }: { session: SessionUser }): JSX.Element {
  const { rows, loading } = useCacheTable<CacheRow>('customers')
  const { query, setQuery, results, tookMs } = useSearch(rows, CUSTOMER_SEARCH)
  const [editing, setEditing] = useState<CacheRow | null>(null)
  const [creating, setCreating] = useState(false)
  const [showArchived, setShowArchived] = useState(false)

  // Archivierte Kunden sind nicht weg, nur aus dem Weg. Wer sie sucht,
  // findet sie über den Schalter.
  const visible = showArchived ? results : results.filter((c) => c.active !== false)
  const archivedCount = rows.filter((c) => c.active === false).length

  return (
    <>
      <div className="card">
        <div className="listhead">
          <h2>Kunden</h2>
          <span className="muted">{rows.length - archivedCount}</span>
        </div>
        <input
          autoComplete="off"
          id="kunde-suche"
          type="search"
          placeholder="Name, Ort, Strasse, Telefon …"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query && (
          <p className="muted small">
            {visible.length} Treffer · lokal gesucht in {tookMs} ms
          </p>
        )}
        {archivedCount > 0 && (
          <label className="inline">
            <input
              autoComplete="off"
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Archivierte anzeigen ({archivedCount})
          </label>
        )}

        {loading ? (
          <p className="muted">Wird geladen …</p>
        ) : visible.length === 0 ? (
          <p className="muted">
            {query ? `Kein Kunde mit „${query}“.` : 'Noch keine Kunden erfasst.'}
          </p>
        ) : (
          <ul className="records">
            {visible.map((c) => (
              <li key={c.id}>
                <button type="button" onClick={() => setEditing(c)}>
                  <span className="rec-name">
                    {String(c.name)}
                    {c.pending === true && <span className="pill small">wartet</span>}
                    {c.oneOff === true && <span className="pill small">Einmal-Adresse</span>}
                    {c.active === false && <span className="pill small">archiviert</span>}
                  </span>
                  <span className="rec-sub">
                    {[c.street, [c.zip, c.city].filter(Boolean).join(' '), c.phone]
                      .filter((p) => p && String(p).trim())
                      .join(' · ')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <ActionButton
          variant="secondary"
          labels={{ idle: 'Neuer Kunde', busy: 'Öffnet …' }}
          onAction={async () => setCreating(true)}
        />
      </div>

      {(creating || editing) && (
        <CustomerForm
          session={session}
          record={editing}
          onDone={() => {
            setCreating(false)
            setEditing(null)
          }}
        />
      )}
    </>
  )
}

/**
 * Das Kundenformular.
 *
 * Es wird von zwei Bildschirmen benutzt: der schmalen Kundenliste und dem
 * Kundenblatt im Büro. Zwei Fassungen hiessen, dass ein neues Feld an einer
 * Stelle fehlt – und zwar an der, die seltener geöffnet wird, also erst
 * Monate später auffällt.
 */
export function CustomerForm({
  session,
  record,
  onDone
}: {
  session: SessionUser
  record: CacheRow | null
  onDone: () => void
}): JSX.Element {
  const { values, set } = useForm<CustomerFields>(
    record
      ? {
          name: String(record.name ?? ''),
          contact: (record.contact as string) ?? '',
          street: (record.street as string) ?? '',
          zip: (record.zip as string) ?? '',
          city: (record.city as string) ?? '',
          email: (record.email as string) ?? '',
          phone: (record.phone as string) ?? '',
          notes: (record.notes as string) ?? '',
          oneOff: record.oneOff === true
        }
      : EMPTY
  )
  const ortschaften = useOrtschaften()
  const strassen = useStrassen()

  const actor = { tenantId: session.tenantId, userId: session.userId }

  return (
    <div className="card">
      <h2>{record ? 'Kunde bearbeiten' : 'Neuer Kunde'}</h2>

      <label htmlFor="k-name">Name oder Firma</label>
      <input
        autoComplete="off"
        id="k-name"
        value={values.name}
        onChange={(e) => set('name', e.target.value)}
      />

      <label htmlFor="k-contact">Ansprechperson</label>
      <input
        autoComplete="off"
        id="k-contact"
        value={values.contact ?? ''}
        onChange={(e) => set('contact', e.target.value)}
      />

      <label htmlFor="k-street">Strasse</label>
      <StrassenFeld
        id="k-street"
        wert={values.street ?? ''}
        aufWert={(w) => set('street', w)}
        aufAdresse={(a) => {
          set('street', a.strasse)
          if (a.plz) set('zip', a.plz)
          if (a.ort) set('city', a.ort)
        }}
        bestand={strassen}
      />

      <OrtsFeldPaar
        praefix="k"
        ids={{ plz: 'k-zip', ort: 'k-city' }}
        plz={values.zip ?? ''}
        ort={values.city ?? ''}
        aufAenderung={({ plz, ort }) => {
          set('zip', plz)
          set('city', ort)
        }}
        ortschaften={ortschaften}
        klassen={{ reihe: 'row', feld: '', plz: 'narrow', ort: 'grow' }}
      />

      <label htmlFor="k-email">E-Mail</label>
      <input
        autoComplete="off"
        id="k-email"
        type="email"
        inputMode="email"
        value={values.email ?? ''}
        onChange={(e) => set('email', e.target.value)}
      />

      <label htmlFor="k-phone">Telefon</label>
      <input
        autoComplete="off"
        id="k-phone"
        inputMode="tel"
        value={values.phone ?? ''}
        onChange={(e) => set('phone', e.target.value)}
      />

      <label htmlFor="k-notes">Notizen</label>
      <textarea
        id="k-notes"
        rows={2}
        value={values.notes ?? ''}
        onChange={(e) => set('notes', e.target.value)}
      />

      <div className="actions-row">
        <ActionButton
          id="btn-kunde-speichern"
          labels={{ idle: 'Speichern', busy: 'Speichert …', success: 'Gespeichert' }}
          onAction={async () => {
            if (!values.name.trim()) {
              notify.warn('Ohne Namen geht es nicht.')
              return
            }
            if (record) {
              await updateCustomer(actor, String(record.id), values, Number(record.version ?? 1))
            } else {
              await createCustomer(actor, values)
            }
            onDone()
          }}
          onError={(err) => notifyError(err)}
        />
        <ActionButton
          variant="quiet"
          labels={{ idle: 'Abbrechen', busy: '…' }}
          onAction={async () => onDone()}
        />
        {record && (
          <ActionButton
            variant="quiet"
            actionClass="B"
            className="danger"
            labels={{
              idle: 'Löschen',
              busy: 'Prüft Verwendung …',
              explain: 'Suche Belege …',
              success: 'Erledigt'
            }}
            onAction={async () => {
              const r = await removeCustomer(actor, String(record.id))
              // Der Server sagt, was passiert ist – die Oberfläche gibt es
              // weiter, statt eigene Vermutungen anzustellen.
              if (r.disposal === 'geloescht') notify.ok(r.message)
              else notify.warn(r.message)
              onDone()
            }}
            onError={(err) => notifyError(err)}
          />
        )}
      </div>
      {record && (
        <p className="muted small">
          Gelöscht wird nur, was noch nirgends verwendet wird. Steht der Kunde schon auf einem
          Projekt oder einem Beleg, wird er archiviert – er verschwindet aus den Auswahllisten,
          bleibt aber überall dort sichtbar, wo er bereits steht.
        </p>
      )}
    </div>
  )
}
